/**
 * Template Preview Panel Component
 * Displays preview of generated code with file type tabs and naming convention options
 */

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import IconProvider from '@/components/ui/IconProvider';
import { ExportTemplate, NamingConvention } from '@/types/exportTemplates';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getPlatformLanguage } from './utils/syntaxHighlighting';

interface TemplatePreviewPanelProps {
  template: ExportTemplate;
  previewContent: string;
  translationKeysPreview: string;
  typesPreview: string;
  tagsPreview: string;
  dialogDatabasePreview: string;
  dialogVariablesPreview: string;
  previewFileType: 'database' | 'translations' | 'types' | 'tags' | 'dialogs' | 'variables';
  setPreviewFileType: (type: 'database' | 'translations' | 'types' | 'tags' | 'dialogs' | 'variables') => void;
  previewNamingConvention: NamingConvention;
  setPreviewNamingConvention: (convention: NamingConvention) => void;
  isGeneratingPreview: boolean;
  onGeneratePreview: () => void;
  effectiveTheme: string;
}

export const TemplatePreviewPanel = ({
  template,
  previewContent,
  translationKeysPreview,
  typesPreview,
  tagsPreview,
  dialogDatabasePreview,
  dialogVariablesPreview,
  previewFileType,
  setPreviewFileType,
  previewNamingConvention,
  setPreviewNamingConvention,
  isGeneratingPreview,
  onGeneratePreview,
  effectiveTheme
}: TemplatePreviewPanelProps) => {
  const syntaxTheme = effectiveTheme === 'dark' ? vscDarkPlus : vs;

  return (
    <div className="space-y-4 mt-4">
      {/* Preview Settings */}
      <div className="p-4 border border-border rounded bg-accent/20 space-y-4">
        <Label className="font-semibold">Preview Settings</Label>
        <p className="text-xs text-muted-foreground">
          Configure namespace and class name in the <strong>Output Structure</strong> tab. The preview uses those settings.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label className="text-sm">Naming Convention (Preview Only)</Label>
            <Select
              value={previewNamingConvention}
              onValueChange={(value) => {
                setPreviewNamingConvention(value as NamingConvention);
                onGeneratePreview();
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="original">Original (As Written)</SelectItem>
                <SelectItem value="camelCase">camelCase</SelectItem>
                <SelectItem value="PascalCase">PascalCase</SelectItem>
                <SelectItem value="snake_case">snake_case</SelectItem>
                <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              This is for preview only. Set the actual naming convention in the export settings.
            </p>
          </div>
        </div>
        <Button
          onClick={onGeneratePreview}
          variant="outline"
          size="sm"
          disabled={isGeneratingPreview}
          className="gap-2"
        >
          {isGeneratingPreview ? (
            <>
              <IconProvider iconstring="fa-spinner" className="animate-spin" size={12} />
              Generating...
            </>
          ) : (
            <>
              <IconProvider iconstring="fa-refresh" size={12} />
              Refresh Preview
            </>
          )}
        </Button>
      </div>
      
      {/* Preview Output */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Generated Output</Label>
          <span className="text-xs text-muted-foreground">
            Showing preview with first 10 items + 5 dialogs
          </span>
        </div>
        
        {/* File Type Toggle (only show if additional files are available) */}
        {(translationKeysPreview || typesPreview || tagsPreview || dialogDatabasePreview || dialogVariablesPreview) && template?.platform === 'unity' && (
          <div className="flex gap-2 p-1 bg-muted/30 rounded-lg w-fit">
            <Button
              variant={previewFileType === 'database' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPreviewFileType('database')}
              className="text-xs h-7"
            >
              <IconProvider iconstring="fa-database" size={12} className="mr-1" />
              {template.databaseSettings?.className || 'Database'}.cs
            </Button>
            {dialogDatabasePreview && (
              <Button
                variant={previewFileType === 'dialogs' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewFileType('dialogs')}
                className="text-xs h-7"
              >
                <IconProvider iconstring="fa-comments" size={12} className="mr-1" />
                DialogDatabase.cs
              </Button>
            )}
            {dialogVariablesPreview && (
              <Button
                variant={previewFileType === 'variables' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewFileType('variables')}
                className="text-xs h-7"
              >
                <IconProvider iconstring="fa-sliders" size={12} className="mr-1" />
                DialogVariables.cs
              </Button>
            )}
            {translationKeysPreview && (
              <Button
                variant={previewFileType === 'translations' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewFileType('translations')}
                className="text-xs h-7"
              >
                <IconProvider iconstring="fa-language" size={12} className="mr-1" />
                {template.databaseSettings?.translationKeysFilename || 'TranslationKeys.cs'}
              </Button>
            )}
            {typesPreview && (
              <Button
                variant={previewFileType === 'types' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewFileType('types')}
                className="text-xs h-7"
              >
                <IconProvider iconstring="fa-tag" size={12} className="mr-1" />
                Types.cs
              </Button>
            )}
            {tagsPreview && (
              <Button
                variant={previewFileType === 'tags' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewFileType('tags')}
                className="text-xs h-7"
              >
                <IconProvider iconstring="fa-tags" size={12} className="mr-1" />
                Tags.cs
              </Button>
            )}
          </div>
        )}
        
        <div className="rounded-lg border border-border overflow-hidden">
          {isGeneratingPreview ? (
            <div className="p-8 text-center text-muted-foreground">
              <IconProvider iconstring="fa-spinner" className="animate-spin inline-block mr-2" size={16} />
              Generating preview...
            </div>
          ) : (previewContent || translationKeysPreview || typesPreview || tagsPreview || dialogDatabasePreview || dialogVariablesPreview) ? (
            <SyntaxHighlighter
              language={getPlatformLanguage(template?.platform || 'unity')}
              style={syntaxTheme}
              customStyle={{
                margin: 0,
                fontSize: '0.75rem',
                maxHeight: '500px',
              }}
              showLineNumbers={true}
            >
              {
                previewFileType === 'translations' && translationKeysPreview ? translationKeysPreview :
                previewFileType === 'types' && typesPreview ? typesPreview :
                previewFileType === 'tags' && tagsPreview ? tagsPreview :
                previewFileType === 'dialogs' && dialogDatabasePreview ? dialogDatabasePreview :
                previewFileType === 'variables' && dialogVariablesPreview ? dialogVariablesPreview :
                previewContent
              }
            </SyntaxHighlighter>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <IconProvider iconstring="fa-code" size={24} className="mb-2" />
              <p>No preview available</p>
              <p className="text-xs mt-1">Click "Refresh Preview" to generate</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Info */}
      <div className="p-3 bg-muted/30 rounded text-xs text-muted-foreground">
        <IconProvider iconstring="fa-info-circle" size={12} className="inline mr-1" />
        This preview shows how your data will be exported using the current template settings.
        The actual export will include all items in your library.
      </div>
    </div>
  );
};
